import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core';

export enum ErrorType {
    EXPIRED = 'expired',
    COMPLETED = 'completed',
    NOT_FOUND = 'not-found'
}

interface ErrorContent {
    title: { en: string; he: string };
    message: { en: string; he: string };
    icon: string;
}

@Component({
    selector: 'que-error-page',
    imports: [CommonModule, TranslateDirective, TranslatePipe],
    templateUrl: './error-page.component.html',
    styleUrls: ['./error-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ErrorPageComponent {
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    #translate = inject(TranslateService);

    errorType = signal<ErrorType>(ErrorType.NOT_FOUND);
    currentLang = signal<'en' | 'he'>('en');

    errorContent: Record<ErrorType, ErrorContent> = {
        [ErrorType.EXPIRED]: {
            title: {
                en: 'Questionnaire Expired',
                he: 'השאלון פג תוקף'
            },
            message: {
                en: 'Sorry, the completion due date for this questionnaire has passed.',
                he: 'מצטערים, תאריך ההגשה של שאלון זה עבר.'
            },
            icon: '⏰'
        },
        [ErrorType.COMPLETED]: {
            title: {
                en: 'Questionnaire Completed',
                he: 'השאלון כבר הושלם'
            },
            message: {
                en: 'This questionnaire has already been completed successfully.',
                he: 'שאלון זה כבר הושלם בהצלחה.'
            },
            icon: '✓'
        },
        [ErrorType.NOT_FOUND]: {
            title: {
                en: 'Page Not Found',
                he: 'הדף לא נמצא'
            },
            message: {
                en: 'The page you are looking for does not exist or is not available.',
                he: 'הדף שאתה מחפש לא קיים או שאינו זמין.'
            },
            icon: '❌'
        }
    };

    ngOnInit() {
        // קבלת סוג השגיאה מה-route
        const errorTypeParam = this.route.snapshot.queryParams['type'] as ErrorType;
        if (errorTypeParam && this.errorContent[errorTypeParam]) {
            this.errorType.set(errorTypeParam);
        }

        // קבלת השפה הנוכחית
        const lang = this.#translate.currentLang() || this.#translate.fallbackLang() || 'en';
        this.currentLang.set(lang as 'en' | 'he');

        // האזנה לשינויי שפה
        this.#translate.onLangChange.subscribe((event) => {
            this.currentLang.set(event.lang as 'en' | 'he');
        });
    }

    get currentError(): ErrorContent {
        return this.errorContent[this.errorType()];
    }

    goToMain() {
        this.router.navigate(['/main']);
    }
}
